import { calcApplicableBonus } from '@/hooks/useWallet';
import type { GiftCode } from '@/lib/supabase';
import type { CatProduct } from '@/mocks/categoryProducts';
import type { Address } from '../types';
import { getVariantStock } from '../utils/productUtils';

interface InstantOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: CatProduct;
  selectedSize: string;
  setSelectedSize: (s: string) => void;
  savedAddress: Address | null;
  walletBalance: number;
  applyBonusInstant: boolean;
  setApplyBonusInstant: (v: boolean) => void;
  giftCodeInstant: string;
  setGiftCodeInstant: (v: string) => void;
  giftCodeInstantData: GiftCode | null;
  setGiftCodeInstantData: (v: GiftCode | null) => void;
  giftCodeInstantDiscount: number;
  setGiftCodeInstantDiscount: (v: number) => void;
  giftCodeInstantMsg: string;
  setGiftCodeInstantMsg: (v: string) => void;
  giftCodeInstantLoading: boolean;
  onApplyGiftCode: (price: number) => void;
  onConfirmOrder: (size: string) => void;
  onChangeAddress: () => void;
}

export default function InstantOrderModal({
  isOpen, onClose, product, selectedSize, setSelectedSize,
  savedAddress, walletBalance, applyBonusInstant, setApplyBonusInstant,
  giftCodeInstant, setGiftCodeInstant, giftCodeInstantData, setGiftCodeInstantData,
  giftCodeInstantDiscount, setGiftCodeInstantDiscount, giftCodeInstantMsg, setGiftCodeInstantMsg,
  giftCodeInstantLoading, onApplyGiftCode, onConfirmOrder, onChangeAddress,
}: InstantOrderModalProps) {
  if (!isOpen) return null;

  const instantWalletDiscount = applyBonusInstant ? calcApplicableBonus(walletBalance, product.price) : 0;
  const instantFinalPayable = product.price - instantWalletDiscount - giftCodeInstantDiscount;
  const productDiscount = product.originalPrice - product.price;
  const totalSaved = productDiscount + instantWalletDiscount + giftCodeInstantDiscount;
  const instantScratchPct = instantFinalPayable < 500 ? 7 : 10;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl max-h-[92vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-green-500 to-emerald-600 flex items-center justify-center">
                <i className="ri-flashlight-fill text-white text-sm"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900">Instant Order</h3>
            </div>
            <button onClick={onClose} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-gray-200 transition-colors cursor-pointer">
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>

          {/* Product Info */}
          <div className="flex gap-4 mb-5 p-3 bg-gray-50 rounded-xl border border-gray-100">
            <img src={product.image} alt={product.name} className="w-20 h-28 object-cover object-top rounded-lg flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-gray-900 text-base leading-tight mb-1">{product.name}</h4>
              <p className="text-gray-400 text-xs mb-2">{product.category}</p>
              <div className="flex items-baseline gap-2">
                <span className="text-primary-pink font-bold text-xl">₹{product.price}</span>
                <span className="text-gray-400 text-xs line-through">₹{product.originalPrice}</span>
              </div>
              <div className="mt-1 inline-flex items-center gap-1 bg-green-50 border border-green-200 rounded-full px-2 py-0.5">
                <i className="ri-price-tag-3-fill text-green-600 text-[10px]"></i>
                <span className="text-green-600 text-[10px] font-semibold">Save ₹{productDiscount} ({Math.round((1 - product.price / product.originalPrice) * 100)}% off)</span>
              </div>
            </div>
          </div>

          {/* Size Selection */}
          <div className="mb-5">
            <label className="block text-sm font-semibold text-gray-700 mb-3">Select Size</label>
            <div className="flex gap-2 flex-wrap mb-3">
              {product.sizes.map(size => {
                let sizeOOS = false;
                if (product.stockBreakdown) {
                  const sv = getVariantStock(product.stockBreakdown, product.colors ?? [], product.sizes, product.selectedColor?.name, size);
                  sizeOOS = sv === 0;
                }
                return (
                  <div key={size} className="relative">
                    <button onClick={() => !sizeOOS && setSelectedSize(size)} disabled={sizeOOS}
                      className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-all cursor-pointer ${
                        selectedSize === size ? 'bg-gray-900 text-white' :
                        sizeOOS ? 'bg-gray-100 text-gray-300 cursor-not-allowed' :
                        'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}>
                      {size}
                      {sizeOOS && (
                        <div className="absolute inset-0 flex items-center justify-center overflow-hidden rounded-full pointer-events-none">
                          <div className="w-full h-0.5 bg-gray-300 absolute rotate-45"></div>
                        </div>
                      )}
                    </button>
                    {sizeOOS && !product.outOfStock && (
                      <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500/90 text-white text-[8px] font-bold flex items-center justify-center">
                        <i className="ri-close-line text-[8px]"></i>
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="flex items-start gap-2 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2.5">
              <i className="ri-exchange-line text-emerald-600 text-sm mt-0.5 shrink-0"></i>
              <p className="text-emerald-700 text-xs leading-relaxed">
                <span className="font-bold">Smart Size:</span> Wrong size? Delivery partner replaces at your door instantly.
              </p>
            </div>
          </div>

          {/* Delivery Address */}
          {savedAddress && (
            <div className="mb-5 p-3 bg-green-50 rounded-xl border border-green-200">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-sm font-semibold text-gray-800 flex items-center gap-1">
                  <i className="ri-map-pin-fill text-green-600"></i>Delivery Address
                </span>
                <button onClick={onChangeAddress} className="text-xs text-green-700 font-semibold hover:underline cursor-pointer">Change</button>
              </div>
              <p className="text-gray-700 text-xs font-medium">{savedAddress.fullName} · {savedAddress.phone}</p>
              <p className="text-gray-500 text-xs leading-relaxed">{savedAddress.address}, {savedAddress.city} - {savedAddress.pincode}</p>
            </div>
          )}

          {/* Gift Code */}
          <div className="mb-4 bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 p-4">
            <p className="text-gray-800 dark:text-gray-200 text-sm font-bold mb-2 flex items-center gap-2">
              <i className="ri-coupon-3-line text-pink-500"></i>Gift Code Apply Karo
            </p>
            {giftCodeInstantData ? (
              <div className="flex items-center gap-2 bg-green-50 border border-green-300 rounded-xl px-3 py-2.5">
                <i className="ri-check-line text-green-600"></i>
                <span className="text-green-700 font-bold text-sm flex-1">{giftCodeInstant} — ₹{giftCodeInstantDiscount} off!</span>
                <button onClick={() => { setGiftCodeInstantData(null); setGiftCodeInstantDiscount(0); setGiftCodeInstant(''); setGiftCodeInstantMsg(''); }} className="text-gray-400 hover:text-red-500 cursor-pointer"><i className="ri-close-line"></i></button>
              </div>
            ) : (
              <div className="flex gap-2">
                <input type="text" value={giftCodeInstant} onChange={e => setGiftCodeInstant(e.target.value.toUpperCase())}
                  onKeyDown={e => e.key === 'Enter' && onApplyGiftCode(product.price)}
                  placeholder="Enter gift code..."
                  className="flex-1 px-3 py-2.5 rounded-xl border border-gray-200 text-gray-900 text-sm font-mono uppercase focus:border-pink-400 outline-none" />
                <button onClick={() => onApplyGiftCode(product.price)} disabled={giftCodeInstantLoading || !giftCodeInstant.trim()}
                  className="px-4 py-2.5 rounded-xl bg-pink-500 text-white font-bold text-sm cursor-pointer hover:bg-pink-600 transition-all disabled:opacity-50 whitespace-nowrap">
                  {giftCodeInstantLoading ? <i className="ri-loader-4-line animate-spin"></i> : 'Apply'}
                </button>
              </div>
            )}
            {giftCodeInstantMsg && !giftCodeInstantData && <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1"><i className="ri-error-warning-line"></i>{giftCodeInstantMsg}</p>}
          </div>

          {/* Wallet */}
          <div className={`mb-4 rounded-2xl p-4 border ${walletBalance > 0 ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-100'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <i className="ri-wallet-3-fill text-amber-500 text-lg"></i>
                <div>
                  <p className="text-gray-800 text-sm font-semibold">Wallet Balance</p>
                  <p className={`text-xs ${walletBalance > 0 ? 'text-amber-600' : 'text-gray-400'}`}>
                    {walletBalance > 0 ? `₹${walletBalance} available (deposit + cashback)` : 'No balance — add from Profile → Wallet'}
                  </p>
                </div>
              </div>
              {walletBalance > 0 && (
                <button onClick={() => setApplyBonusInstant(!applyBonusInstant)}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold cursor-pointer transition-all whitespace-nowrap ${applyBonusInstant ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}>
                  {applyBonusInstant ? '✓ Applied' : 'Apply'}
                </button>
              )}
            </div>
            {applyBonusInstant && <p className="mt-1.5 text-green-600 text-xs flex items-center gap-1"><i className="ri-checkbox-circle-fill"></i>₹{calcApplicableBonus(walletBalance, product.price)} wallet balance applied!</p>}
          </div>

          <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 mb-4">
            <i className="ri-coupon-4-fill text-amber-500 text-sm"></i>
            <p className="text-amber-700 text-xs">
              <span className="font-bold">Mystery scratch card</span> milega! Scratch karke <span className="font-bold">{instantScratchPct}% bonus</span> reveal karo!
            </p>
          </div>

          {/* Order Summary */}
          <div className="border-t border-gray-100 pt-4 mb-4 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-gray-500 text-sm">MRP</span>
              <span className="text-gray-400 text-sm line-through">₹{product.originalPrice}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-500 text-sm">Product Discount</span>
              <span className="text-green-600 text-sm font-semibold">- ₹{productDiscount}</span>
            </div>
            {giftCodeInstantDiscount > 0 && (
              <div className="flex items-center justify-between bg-pink-50 border border-pink-200 rounded-xl px-3 py-2">
                <span className="text-pink-700 text-sm flex items-center gap-1"><i className="ri-coupon-3-fill text-pink-500"></i>Gift Code ({giftCodeInstant})</span>
                <span className="text-pink-700 text-sm font-bold">- ₹{giftCodeInstantDiscount}</span>
              </div>
            )}
            {instantWalletDiscount > 0 && (
              <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
                <span className="text-amber-700 text-sm flex items-center gap-1"><i className="ri-wallet-3-fill text-amber-500"></i>Wallet Balance Applied</span>
                <span className="text-amber-700 text-sm font-bold">- ₹{instantWalletDiscount}</span>
              </div>
            )}
            <div className="flex items-center justify-between">
              <span className="text-gray-500 text-sm">Delivery Charges</span>
              <span className="text-green-600 text-sm font-semibold">FREE</span>
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-gray-100">
              <span className="text-gray-900 font-bold">Total to Pay</span>
              <div className="text-right">
                {instantWalletDiscount > 0 && <p className="text-gray-400 text-xs line-through text-right">₹{product.price}</p>}
                <span className="text-gray-900 font-bold text-xl">₹{instantFinalPayable}</span>
              </div>
            </div>
            <div className="flex items-center justify-center gap-2 bg-green-50 border border-green-200 rounded-2xl py-2.5 px-4">
              <i className="ri-gift-line text-green-600 text-base"></i>
              <span className="text-green-700 text-sm font-bold">You save ₹{totalSaved} on this order!</span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button onClick={onClose}
              className="flex-1 py-3 rounded-xl border border-gray-200 text-gray-600 font-medium hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">
              Cancel
            </button>
            <button onClick={() => selectedSize && onConfirmOrder(selectedSize)} disabled={!selectedSize}
              className="flex-1 py-4 rounded-xl bg-gradient-to-r from-green-500 to-emerald-600 text-white font-bold hover:shadow-lg hover:shadow-green-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1 whitespace-nowrap cursor-pointer">
              <i className="ri-flashlight-fill"></i>
              {selectedSize ? `Order Now · ₹${instantFinalPayable}` : 'Select Size First'}
            </button>
          </div>
          <p className="text-center text-gray-400 text-xs mt-2">Cash on Delivery · Pay when order arrives</p>
        </div>
      </div>
    </div>
  );
}
